import { FC } from 'react';

import { type RoleRequestRecord, RoleRequestStatus } from '@/api/role-requests/types';
import { Avatar, Badge, EmptyState, Loader, theme, Typography } from '@/shared/design-system';
import { describeRoleRequest } from '@/shared/modules/role-requests/role-request-summary';
import { requestPartyLabel } from '@/utils/role-request-activity';

import { CenteredState } from '../../styled';
import { ActivityEntry, ActivityList, ActivityText } from './styled';

export const NO_ACTIVITY_TITLE = 'No changes yet';
export const NO_ACTIVITY_MESSAGE =
  'Every change to this role and its members is recorded here once someone requests it.';
export const ACTIVITY_LOAD_ERROR = "Couldn't load this role's history";

const STATUS_BADGE = {
  [RoleRequestStatus.Pending]: { label: 'Pending', color: 'yellow' },
  [RoleRequestStatus.Approved]: { label: 'Approved', color: 'primary' },
  [RoleRequestStatus.Rejected]: { label: 'Rejected', color: 'destructive' },
} as const;

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );

export const activityHeadline = (request: RoleRequestRecord) => {
  const { action, detail } = describeRoleRequest(request);
  const target = request.target ? ` for ${requestPartyLabel(request.target)}` : '';

  return `${action}: ${detail}${target}`;
};

export const activityOutcome = (request: RoleRequestRecord) => {
  const asked = `Requested by ${requestPartyLabel(request.initiator)} · ${formatDate(request.initiatedAt)}`;
  if (request.status === RoleRequestStatus.Pending) return `${asked} · waiting for an approver`;
  if (request.autoApproved) return `${asked} · applied right away`;

  const decider = request.decider ? requestPartyLabel(request.decider) : 'an approver';
  if (request.status === RoleRequestStatus.Rejected) {
    return `${asked} · rejected by ${decider}${request.rejectionReason ? `: “${request.rejectionReason}”` : ''}`;
  }

  return `${asked} · approved by ${decider}`;
};

export type RoleActivityProps = {
  requests: readonly RoleRequestRecord[];
  isLoading: boolean;
  isError: boolean;
};

/** The role's history, read from its role-change requests: who asked, who decided, and why. */
export const RoleActivity: FC<RoleActivityProps> = ({ requests, isLoading, isError }) => {
  if (isLoading) {
    return (
      <CenteredState>
        <Loader />
      </CenteredState>
    );
  }

  if (isError) {
    return (
      <CenteredState>
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {ACTIVITY_LOAD_ERROR}
        </Typography>
      </CenteredState>
    );
  }

  if (!requests.length) {
    return <EmptyState title={NO_ACTIVITY_TITLE} description={NO_ACTIVITY_MESSAGE} />;
  }

  return (
    <ActivityList>
      {requests.map((request) => {
        const status = STATUS_BADGE[request.status];
        const initiator = requestPartyLabel(request.initiator);

        return (
          <ActivityEntry key={request.id}>
            <Avatar size="sm" type="initials" initials={initiator} />
            <ActivityText>
              <Typography variant="body3" color={theme.colors.text.primary}>
                {activityHeadline(request)}
              </Typography>
              <Typography variant="body4" color={theme.colors.text.tertiary}>
                {activityOutcome(request)}
              </Typography>
            </ActivityText>
            <Badge size="sm" variant="subtle" color={status.color} label={status.label} />
          </ActivityEntry>
        );
      })}
    </ActivityList>
  );
};
